import React, { useState, useEffect } from 'react';
import { useSearchParams, Link as RouterLink } from 'react-router-dom';
import {
  Box, TextField, Button, Paper, Typography, CircularProgress,
  List, ListItem, ListItemText, Divider, Table, TableBody,
  TableRow, TableCell, Chip, Grid, Collapse, ToggleButton,
  ToggleButtonGroup, Autocomplete, FormControl, InputLabel, Select, MenuItem,
  Pagination, Accordion, AccordionSummary, AccordionDetails, Checkbox, useTheme
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import TuneIcon from '@mui/icons-material/Tune';
import SortIcon from '@mui/icons-material/Sort';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { searchHouseQiw } from '../services/api';
import DateHierarchyFilter from './DateHierarchyFilter';
import PageSizeSelector, { DEFAULT_PAGE_SIZE } from './PageSizeSelector';
import ExportCSVButton from './ExportCSVButton';
import { SaveSearchButton } from './saved-searches';
import RSSButton from './RSSButton';
import { sortFacetsBooleansFirst } from '../utils/facetOrder';

export default function HouseQiwSearch() {
  const theme = useTheme();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchMode, setSearchMode] = useState(searchParams.get('mode') || 'simple');
  const [query, setQuery] = useState(searchParams.get('q') || '');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expandedDetails, setExpandedDetails] = useState({});
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(() => {
    const urlPageSize = parseInt(searchParams.get('pageSize'), 10);
    return [10, 25, 50, 100].includes(urlPageSize) ? urlPageSize : DEFAULT_PAGE_SIZE;
  });
  const [sortOrder, setSortOrder] = useState('asked_date'); // Default: Date (newest)
  const [activeFilters, setActiveFilters] = useState({});
  const [facets, setFacets] = useState({});

  // Date drill-down state
  const [selectedYear, setSelectedYear] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [selectedDay, setSelectedDay] = useState(null);

  // Available options for autocomplete
  const [availableOptions, setAvailableOptions] = useState({
    askedBy: [],
    askedTo: [],
    portfolios: []
  });

  // Advanced form state
  const [form, setForm] = useState({
    keywords: '',
    askedBy: '',
    askedTo: '',
    portfolio: '',
    status: '',
    isAnswered: ''
  });

  // Fetch autocomplete options on mount
  useEffect(() => {
    const fetchOptions = async () => {
      try {
        const data = await searchHouseQiw('*', 1, 100);
        if (data.hits) {
          const askedBy = [...new Set(data.hits.map(h => h.asked_by).filter(Boolean))].sort();
          const askedTo = [...new Set(data.hits.map(h => h.asked_to).filter(Boolean))].sort();
          const portfolios = [...new Set(data.hits.map(h => h.part).filter(Boolean))].sort();
          setAvailableOptions({ askedBy, askedTo, portfolios });
        }
      } catch (error) {
        console.error('Failed to fetch options:', error);
      }
    };
    fetchOptions();
  }, []);

  useEffect(() => {
    const urlQuery = searchParams.get('q');
    if (urlQuery && urlQuery !== '*') {
      handleSearch(urlQuery);
    } else {
      handleSearch('*');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = (field) => (event) => {
    setForm({ ...form, [field]: event.target.value });
  };

  const handleAutocompleteChange = (field) => (event, newValue) => {
    setForm({ ...form, [field]: newValue || '' });
  };

  const buildAdvancedQuery = () => {
    const parts = [];

    if (form.keywords) parts.push(`(${form.keywords})`);
    if (form.askedBy) parts.push(`asked_by:"${form.askedBy}"`);
    if (form.askedTo) parts.push(`asked_to:"${form.askedTo}"`);
    if (form.portfolio) parts.push(`part:"${form.portfolio}"`);
    if (form.status) parts.push(`status:"${form.status}"`);
    if (form.isAnswered !== '') parts.push(`is_answered:${form.isAnswered}`);

    return parts.length > 0 ? parts.join(' AND ') : '*';
  };

  const buildQueryWithFilters = (baseQuery, filters) => {
    const filterParts = [];
    Object.entries(filters).forEach(([field, values]) => {
      if (values.length > 0) {
        const fieldQuery = values.map(v => `${field}:"${v}"`).join(' OR ');
        filterParts.push(`(${fieldQuery})`);
      }
    });

    if (filterParts.length === 0) return baseQuery;
    if (baseQuery === '*') return filterParts.join(' AND ');
    return `(${baseQuery}) AND ${filterParts.join(' AND ')}`;
  };

  const handleSearch = async (searchQuery = null, newPage = 1, filters = activeFilters, year = selectedYear, month = selectedMonth, pageSize = perPage, sort = sortOrder) => {
    let baseQuery;
    if (searchMode === 'simple') {
      baseQuery = searchQuery !== null ? searchQuery : (query || '*');
    } else {
      baseQuery = buildAdvancedQuery();
    }

    const q = buildQueryWithFilters(baseQuery, filters);

    setLoading(true);
    try {
      const data = await searchHouseQiw(q, newPage, pageSize, year, month, sort);
      setResults(data);
      setPage(newPage);
      if (data.facets) {
        setFacets(data.facets);
      }
      const newParams = { q: baseQuery, mode: searchMode };
      if (pageSize !== DEFAULT_PAGE_SIZE) newParams.pageSize = pageSize;
      setSearchParams(newParams);
    } catch (error) {
      console.error('Search error:', error);
    }
    setLoading(false);
  };

  // Date drill-down handlers
  const handleYearClick = (year) => {
    setSelectedYear(year);
    setSelectedMonth(null);
    setSelectedDay(null);
    handleSearch(null, 1, activeFilters, year, null);
  };

  const handleMonthClick = (month) => {
    setSelectedMonth(month);
    setSelectedDay(null);
    const year = month.split('-')[0];
    setSelectedYear(year);
    handleSearch(null, 1, activeFilters, year, month);
  };

  const handleDayClick = (day) => {
    setSelectedDay(day);
  };

  const clearDateDrillDown = () => {
    setSelectedYear(null);
    setSelectedMonth(null);
    setSelectedDay(null);
    handleSearch(null, 1, activeFilters, null, null);
  };

  const handleFacetClick = (field, value) => {
    const newFilters = { ...activeFilters };
    if (!newFilters[field]) newFilters[field] = [];
    const valueIndex = newFilters[field].indexOf(value);
    if (valueIndex > -1) {
      newFilters[field] = newFilters[field].filter(v => v !== value);
      if (newFilters[field].length === 0) delete newFilters[field];
    } else {
      newFilters[field].push(value);
    }
    setActiveFilters(newFilters);
    handleSearch(null, 1, newFilters);
  };

  const clearAllFilters = () => {
    setActiveFilters({});
    handleSearch(null, 1, {});
  };

  const facetToField = (facetName) => {
    const mapping = {
      'asked_by': 'asked_by',
      'asked_to': 'asked_to',
      'portfolios': 'part',
      'statuses': 'status',
      'answered': 'is_answered'
    };
    return mapping[facetName] || facetName;
  };

  const facetDisplayName = (facetName) => {
    const names = {
      'asked_by': 'Asked By',
      'asked_to': 'Asked To',
      'portfolios': 'Portfolio',
      'statuses': 'Status',
      'answered': 'Answered'
    };
    return names[facetName] || facetName;
  };

  const formatFacetValue = (facetName, value) => {
    if (facetName === 'answered') {
      // ES boolean term-agg emits numeric 1/0; also tolerate string/boolean forms
      return value === 1 || value === '1' || value === true || value === 'true' ? 'Yes' : 'No';
    }
    return value;
  };

  const handlePageChange = (event, newPage) => {
    handleSearch(null, newPage);
  };

  const handlePageSizeChange = (newSize) => {
    setPerPage(newSize);
    handleSearch(null, 1, activeFilters, selectedYear, selectedMonth, newSize);
  };

  const handleSortChange = (newSort) => {
    setSortOrder(newSort);
    handleSearch(null, 1, activeFilters, selectedYear, selectedMonth, perPage, newSort);
  };

  const handleReset = () => {
    setQuery('');
    setForm({
      keywords: '',
      askedBy: '',
      askedTo: '',
      portfolio: '',
      status: '',
      isAnswered: ''
    });
    setResults(null);
    setPage(1);
    setActiveFilters({});
    setFacets({});
    setSelectedYear(null);
    setSelectedMonth(null);
    setSelectedDay(null);
    setSortOrder('asked_date');
    setSearchParams({});
  };

  const toggleDetails = (id) => {
    setExpandedDetails(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const truncateContent = (content, maxLength = 300) => {
    if (!content) return '';
    if (content.length <= maxLength) return content;
    return content.substring(0, maxLength) + '...';
  };

  const MetadataRow = ({ label, value }) => {
    if (value === null || value === undefined || value === '' || (Array.isArray(value) && value.length === 0)) return null;
    return (
      <TableRow>
        <TableCell sx={{ fontWeight: 500, color: 'text.secondary', width: '30%', py: 1 }}>{label}</TableCell>
        <TableCell sx={{ py: 1 }}>{Array.isArray(value) ? value.join(', ') : String(value)}</TableCell>
      </TableRow>
    );
  };

  const handleModeChange = (event, newMode) => {
    if (newMode !== null) {
      setSearchMode(newMode);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4">House Questions in Writing</Typography>
        <ToggleButtonGroup
          value={searchMode}
          exclusive
          onChange={handleModeChange}
          size="small"
        >
          <ToggleButton value="simple">
            <SearchIcon sx={{ mr: 1 }} /> Simple
          </ToggleButton>
          <ToggleButton value="advanced">
            <TuneIcon sx={{ mr: 1 }} /> Advanced
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Simple Search */}
      <Collapse in={searchMode === 'simple'}>
        <Paper elevation={1} sx={{ p: 2, mb: 4, borderRadius: 3 }}>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              fullWidth
              placeholder="Search House questions in writing..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              variant="outlined"
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 } }}
            />
            <Button
              variant="contained"
              onClick={() => handleSearch()}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : <SearchIcon />}
              sx={{ borderRadius: 3, px: 4 }}
            >
              Search
            </Button>
            <Button variant="outlined" onClick={handleReset} sx={{ borderRadius: 3 }}>
              Reset
            </Button>
          </Box>
        </Paper>
      </Collapse>

      {/* Advanced Search */}
      <Collapse in={searchMode === 'advanced'}>
        <Paper elevation={1} sx={{ p: 3, mb: 4, borderRadius: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Keywords"
                value={form.keywords}
                onChange={handleChange('keywords')}
                placeholder="Search in question content"
                size="small"
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <Autocomplete
                fullWidth
                size="small"
                options={availableOptions.askedBy}
                value={form.askedBy}
                onChange={handleAutocompleteChange('askedBy')}
                renderInput={(params) => <TextField {...params} label="Asked By" />}
                freeSolo
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <Autocomplete
                fullWidth
                size="small"
                options={availableOptions.askedTo}
                value={form.askedTo}
                onChange={handleAutocompleteChange('askedTo')}
                renderInput={(params) => <TextField {...params} label="Asked To" />}
                freeSolo
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <Autocomplete
                fullWidth
                size="small"
                options={availableOptions.portfolios}
                value={form.portfolio}
                onChange={handleAutocompleteChange('portfolio')}
                renderInput={(params) => <TextField {...params} label="Portfolio" />}
                freeSolo
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Answered</InputLabel>
                <Select value={form.isAnswered} onChange={handleChange('isAnswered')} label="Answered">
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="true">Yes</MenuItem>
                  <MenuItem value="false">No</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <Button
                  variant="contained"
                  onClick={() => handleSearch()}
                  disabled={loading}
                  startIcon={loading ? <CircularProgress size={20} /> : <SearchIcon />}
                  sx={{ borderRadius: 2 }}
                >
                  Search
                </Button>
                <Button variant="outlined" onClick={handleReset} sx={{ borderRadius: 2 }}>
                  Reset
                </Button>
                <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
                  Query: <code>{buildAdvancedQuery() || '(empty)'}</code>
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Paper>
      </Collapse>

      {results && (
        <Grid container spacing={3}>
          {/* Facets Panel */}
          {Object.keys(facets).length > 0 && (
            <Grid item xs={12} md={3}>
              <Paper elevation={1} sx={{ p: 2, borderRadius: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600 }}>
                    Filter Results
                  </Typography>
                  {(Object.keys(activeFilters).length > 0 || selectedYear) && (
                    <Button size="small" onClick={() => { clearAllFilters(); clearDateDrillDown(); }}>Clear All</Button>
                  )}
                </Box>

                {/* Date Hierarchy Filter */}
                {(facets.years?.length > 0 || facets.months?.length > 0 || facets.days?.length > 0) && (
                  <DateHierarchyFilter
                    facets={facets}
                    selectedYear={selectedYear}
                    selectedMonth={selectedMonth}
                    selectedDay={selectedDay}
                    onYearClick={handleYearClick}
                    onMonthClick={handleMonthClick}
                    onDayClick={handleDayClick}
                    onClearAll={clearDateDrillDown}
                  />
                )}

                {sortFacetsBooleansFirst(['asked_by', 'asked_to', 'portfolios', 'statuses', 'answered'], facets).map((facetName) => (
                  facets[facetName] && facets[facetName].length > 0 && (
                    <Accordion key={facetName} defaultExpanded={['asked_by', 'portfolios', 'answered'].includes(facetName)}>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="subtitle2">
                          {facetDisplayName(facetName)}
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails sx={{ pt: 0 }}>
                        <List dense sx={{ py: 0 }}>
                          {facets[facetName].slice(0, 15).map((facet) => (
                            <ListItem key={facet.value} disablePadding>
                              <Checkbox
                                size="small"
                                checked={activeFilters[facetToField(facetName)]?.includes(String(facet.value)) || false}
                                onChange={() => handleFacetClick(facetToField(facetName), String(facet.value))}
                              />
                              <ListItemText
                                primary={`${formatFacetValue(facetName, facet.value)} (${facet.count.toLocaleString()})`}
                                primaryTypographyProps={{ variant: 'body2' }}
                              />
                            </ListItem>
                          ))}
                        </List>
                      </AccordionDetails>
                    </Accordion>
                  )
                ))}
              </Paper>
            </Grid>
          )}

          {/* Results */}
          <Grid item xs={12} md={Object.keys(facets).length > 0 ? 9 : 12}>
            <Paper elevation={1} sx={{ p: 2, borderRadius: 3 }}>
              {/* Row 1: count (left) + pagination (right) */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, flexWrap: 'wrap', gap: 1 }}>
                <Typography variant="subtitle1">
                  {results.total.toLocaleString()} questions found
                </Typography>
                {results.total > perPage && (
                  <Pagination
                    count={Math.ceil(results.total / perPage)}
                    page={page}
                    onChange={handlePageChange}
                    color="primary"
                    size="small"
                  />
                )}
              </Box>
              {/* Row 2: Per page · Sort · Export · Save · RSS */}
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', mb: 2 }}>
                <PageSizeSelector value={perPage} onChange={handlePageSizeChange} />
                <FormControl size="small" sx={{ minWidth: 200 }}>
                  <InputLabel id="houseqiw-sort-label">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <SortIcon fontSize="small" />
                      Sort by
                    </Box>
                  </InputLabel>
                  <Select
                    labelId="houseqiw-sort-label"
                    value={sortOrder}
                    onChange={(e) => handleSortChange(e.target.value)}
                    label="Sort by"
                  >
                    <MenuItem value="asked_date">Date (newest)</MenuItem>
                    <MenuItem value="question_number">Question No.</MenuItem>
                    <MenuItem value="asked_by.keyword">Asked By</MenuItem>
                    <MenuItem value="_score">Relevance</MenuItem>
                  </Select>
                </FormControl>
                <ExportCSVButton
                  dataType="houseqiw"
                  query={query || '*'}
                  totalResults={results.total}
                />
                <SaveSearchButton
                  searchType="houseqiw"
                  queryText={query || '*'}
                  parameters={{ sort: sortOrder, page, per_page: perPage, ...activeFilters }}
                  resultCount={results.total}
                />
                <RSSButton collection="house-qiw" query={query || '*'} />
              </Box>
              <List>
                {results.hits.map((hit, index) => (
                  <React.Fragment key={hit.id}>
                    {index > 0 && <Divider />}
                    <ListItem alignItems="flex-start" sx={{ flexDirection: 'column' }}>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                            <Typography variant="resultTitle">
                              {hit.title || `Question ${hit.question_number || 'N/A'}`}
                            </Typography>
                            <Chip
                              label={hit.is_answered ? 'Answered' : 'Unanswered'}
                              size="small"
                              color={hit.is_answered ? 'success' : 'warning'}
                            />
                            {hit.part && (
                              <Chip label={hit.part} size="small" variant="outlined" />
                            )}
                          </Box>
                        }
                        secondary={
                          <Box sx={{ mt: 1 }}>
                            <Typography variant="body2" color="text.primary" sx={{ fontWeight: 500 }}>
                              Asked by:{' '}
                              {hit.asked_by_id ? (
                                <RouterLink to={`/parliamentarians/${hit.asked_by_id}`} style={{ color: theme.palette.ledger.accentText, textDecoration: 'none' }}>
                                  {hit.asked_by || 'Unknown'}
                                </RouterLink>
                              ) : (
                                hit.asked_by || 'Unknown'
                              )}
                              {hit.asked_to && ` · To: ${hit.asked_to}`}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                              {[
                                hit.asked_date && `Date: ${formatDate(hit.asked_date)}`,
                                hit.question_number && `Question No: ${hit.question_number}`
                              ].filter(Boolean).join(' · ')}
                            </Typography>
                            <Typography className="ps-snippet" variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                              {hit._highlights?.content ? (
                                <span dangerouslySetInnerHTML={{
                                  __html: hit._highlights.content.join(' ... ')
                                }} />
                              ) : (
                                truncateContent(hit.content)
                              )}
                            </Typography>
                          </Box>
                        }
                      />
                      <Button size="small" onClick={() => toggleDetails(hit.id)} sx={{ mt: 1 }}>
                        {expandedDetails[hit.id] ? 'Hide details' : 'Show all details'}
                      </Button>
                      <Collapse in={expandedDetails[hit.id]}>
                        <Box sx={{ mt: 2, width: '100%' }}>
                          <Table size="small">
                            <TableBody>
                              <TableRow>
                                <TableCell colSpan={2} sx={{ bgcolor: '#f5f5f5', fontWeight: 600 }}>
                                  Question Information
                                </TableCell>
                              </TableRow>
                              <MetadataRow label="Title" value={hit.title} />
                              <MetadataRow label="Question Number" value={hit.question_number} />
                              <MetadataRow label="Asked By" value={hit.asked_by} />
                              <MetadataRow label="Asked To" value={hit.asked_to} />
                              <MetadataRow label="Portfolio" value={hit.part} />
                              <MetadataRow label="Asked Date" value={formatDate(hit.asked_date)} />
                              <MetadataRow label="Status" value={hit.status} />
                              <MetadataRow label="Answered" value={hit.is_answered ? 'Yes' : 'No'} />
                              <MetadataRow label="Content Length" value={`${hit.content_length?.toLocaleString()} characters`} />
                            </TableBody>
                          </Table>
                          <Paper sx={{ p: 2, mt: 2, bgcolor: '#fafafa', maxHeight: 400, overflow: 'auto' }}>
                            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                              Full Content
                            </Typography>
                            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                              {hit.content}
                            </Typography>
                          </Paper>
                          {hit.asked_by_id && (
                            <Box sx={{ mt: 2 }}>
                              <Button
                                size="small"
                                variant="outlined"
                                component={RouterLink}
                                to={`/parliamentarians/${hit.asked_by_id}`}
                              >
                                View Parliamentarian Profile
                              </Button>
                            </Box>
                          )}
                        </Box>
                      </Collapse>
                    </ListItem>
                  </React.Fragment>
                ))}
              </List>
            </Paper>
          </Grid>
        </Grid>
      )}
    </Box>
  );
}
